import { ItemInstance, TreeInstance } from '@headless-tree/core';
import React, { useRef } from 'react';
import { TaskCardMultiSelectContextMenu } from 'shared/components/connected/task-card-multi-select-context-menu';
import { InternalTreeItem } from 'shared/components/ui/virtualized-tree-list';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';
import { Task } from 'shared/types/task';
import { useTheme } from 'styled-components';

import { TaskCard } from './task-card';
import * as Styled from './task-card.style';

export type TreeTaskCardProps = React.ComponentProps<typeof TaskCard> & {
  tree: TreeInstance<InternalTreeItem>;
  item: ItemInstance<InternalTreeItem>;
  tasks: Task[];
};

export const TreeTaskCard: React.FC<TreeTaskCardProps> = ({
  tree,
  item,
  tasks,
  hideContextMenu,
  ...taskCardProps
}) => {
  const _isMultiSelected =
    hideContextMenu || tree.getSelectedItems().length > 1;
  const theme = useTheme();
  const selectedTaskItems = useRef<ItemInstance<InternalTreeItem>[]>([]);
  const selectedTasks = useRef<Task[]>([]);

  const { position, open, close } = useContextMenuState({
    disabled: theme.isMobile,
  });

  const handleContextMenu = (e: React.MouseEvent) => {
    // stop the browser's default context menu
    e.preventDefault();
    e.stopPropagation();

    // get all selected tasks
    selectedTaskItems.current = [...tree.getSelectedItems()];
    selectedTasks.current = selectedTaskItems.current
      .map((item) => tasks.find(({ id }) => item.getItemData().sourceId === id))
      .filter(Boolean) as Task[];

    // select item if not selected
    if (!item.isSelected()) {
      tree.setSelectedItems([item.getId()]);
      selectedTaskItems.current.push(item);
      const task = tasks.find(({ id }) => item.getItemData().sourceId === id);
      task && selectedTasks.current.push(task);
    }

    // get the selected items combined
    open(e);
  };

  return (
    <Styled.TreeItemContainer onContextMenu={handleContextMenu}>
      <TaskCard
        {...taskCardProps}
        isMultiSelectable
        hideContextMenu={_isMultiSelected}
        isSelected={item.isSelected()}
      />

      {!!position && (
        <TaskCardMultiSelectContextMenu
          onClose={close}
          location={position}
          tasks={selectedTasks.current}
        />
      )}
    </Styled.TreeItemContainer>
  );
};
